import { useState, useEffect, useMemo, useRef } from "react";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  CalendarIcon,
  Download,
  ExternalLink,
  Loader2,
  MoreHorizontal,
  Pencil,
  Trash2,
  CreditCard,
  ChevronLeft,
  ChevronRight,
  Search,
} from "lucide-react";
import Papa from "papaparse";
import { ProductSelectItems } from "@/components/ProductSelectItems";
import { useBreadcrumb } from "@/contexts/BreadcrumbContext";
import { Link, useSearchParams } from "react-router-dom";
import {
  applyReportDateToSearchParams,
  parseReportDateSearchParams,
  type ReportDatePreset,
} from "@/lib/reportDateFilterUrl";
import { formatPaymentFulfillmentStatus } from "@/lib/paymentFulfillmentDisplay";
import { formatPaymentStatus } from "@/lib/paymentStatusDisplay";
import { normalizeAttendanceStatuses } from "@/lib/ticketAttendance";
import {
  ticketAttendanceLabels,
  ticketAttendanceSelectOptions,
} from "@/lib/ticketAttendanceDisplay";
import { ProductTypeEnum } from "@/types/ProductType";
import type { TicketAttendanceStatus } from "@/types/TicketAttendanceStatus";
import type { DateRange as DayPickerRange } from "react-day-picker";

interface PurchasedProductLine {
  productId?: string;
  variantId?: string;
  quantity?: number;
  description?: string;
  price?: number;
  attendanceStatuses?: TicketAttendanceStatus[];
}

interface CatalogVariant {
  _id: string;
  name?: string;
}

interface CatalogProduct {
  _id: string;
  name?: string;
  type?: string;
  archivedAt?: string | null;
  variants?: CatalogVariant[];
}

interface Payment {
  _id: string;
  lead?: string;
  customerEmail?: string;
  amount: number;
  currency?: string;
  paymentIntentId?: string;
  createdAt: string;
  purchasedProducts?: PurchasedProductLine[];
  fulfillmentStatus?: string | null;
  status?: string | null;
}

function serializePaymentFieldValue(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function serializeLeaf(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** Paths like `shipping.name`, `shipping.address.line1` with scalar leaves only. */
function collectShippingLeafPaths(prefix: string, value: unknown, keys: Set<string>): void {
  if (!isPlainObject(value)) return;
  for (const [k, v] of Object.entries(value)) {
    const path = `${prefix}.${k}`;
    if (isPlainObject(v)) {
      collectShippingLeafPaths(path, v, keys);
    } else {
      keys.add(path);
    }
  }
}

function collectPurchasedProductPaths(doc: Record<string, unknown>, keys: Set<string>): void {
  const pp = doc.purchasedProducts;
  if (!Array.isArray(pp)) return;
  pp.forEach((line, i) => {
    if (!isPlainObject(line)) return;
    for (const k of Object.keys(line)) {
      keys.add(`purchasedProducts[${i}].${k}`);
    }
  });
}

function collectPaymentsCsvFields(docs: Record<string, unknown>[]): string[] {
  const keys = new Set<string>();
  for (const doc of docs) {
    for (const k of Object.keys(doc)) {
      if (k === "purchasedProducts" || k === "shipping") continue;
      keys.add(k);
    }
    const sh = doc.shipping;
    if (isPlainObject(sh)) {
      collectShippingLeafPaths("shipping", sh, keys);
    }
    collectPurchasedProductPaths(doc, keys);
  }

  const columnSortKey = (col: string): [number, string] => {
    const m = /^purchasedProducts\[(\d+)\]\.(.+)$/.exec(col);
    if (m) {
      return [
        2,
        `${Number(m[1]).toString().padStart(6, "0")}\0${m[2]}`,
      ];
    }
    if (col.startsWith("shipping.")) return [1, col];
    return [0, col];
  };

  return [...keys].sort((a, b) => {
    const [ta, ka] = columnSortKey(a);
    const [tb, kb] = columnSortKey(b);
    if (ta !== tb) return ta - tb;
    return ka.localeCompare(kb);
  });
}

function csvCellForColumn(doc: Record<string, unknown>, col: string): string {
  const ppMatch = /^purchasedProducts\[(\d+)\]\.(.+)$/.exec(col);
  if (ppMatch) {
    const idx = Number(ppMatch[1]);
    const prop = ppMatch[2];
    const pp = doc.purchasedProducts;
    if (!Array.isArray(pp) || !isPlainObject(pp[idx])) return "";
    return serializeLeaf((pp[idx] as Record<string, unknown>)[prop]);
  }

  if (col.startsWith("shipping.")) {
    const rest = col.slice("shipping.".length);
    const parts = rest.split(".");
    let cur: unknown = doc.shipping;
    for (const p of parts) {
      if (!isPlainObject(cur)) return "";
      cur = cur[p];
    }
    return serializeLeaf(cur);
  }

  return serializePaymentFieldValue(doc[col]);
}

/** Full Payment documents from `/api/payments`; `purchasedProducts` and `shipping` are one column per leaf path. */
function paymentsDocumentsToCsv(payments: Record<string, unknown>[]): string {
  const fields = collectPaymentsCsvFields(payments);
  const data = payments.map((doc) => {
    const row: Record<string, string> = {};
    for (const field of fields) {
      row[field] = csvCellForColumn(doc, field);
    }
    return row;
  });
  return Papa.unparse({ fields, data });
}

function lineMatchesProductFilter({
  line,
  productId,
  variantId,
}: {
  line: PurchasedProductLine;
  productId: string;
  variantId: string;
}): boolean {
  if (!line.productId || String(line.productId) !== productId) return false;
  if (variantId) {
    return String(line.variantId ?? "") === variantId;
  }
  return true;
}

function buildPurchasedProductsPayload({
  payment,
  productById,
}: {
  payment: Payment;
  productById: Map<string, CatalogProduct>;
}) {
  return (payment.purchasedProducts ?? []).map((line) => {
    const quantity = line.quantity ?? 1;
    const payload: {
      productId?: string;
      variantId?: string;
      description: string;
      quantity: number;
      price?: number;
      attendanceStatuses?: TicketAttendanceStatus[];
    } = {
      description: line.description ?? "",
      quantity,
    };
    if (line.productId) payload.productId = String(line.productId);
    if (line.variantId) payload.variantId = String(line.variantId);
    if (line.price != null) payload.price = line.price;

    const product = line.productId ? productById.get(String(line.productId)) : undefined;
    if (product?.type === ProductTypeEnum.TICKET) {
      payload.attendanceStatuses = normalizeAttendanceStatuses({
        statuses: line.attendanceStatuses,
        quantity,
      });
    } else if (line.attendanceStatuses?.length) {
      payload.attendanceStatuses = line.attendanceStatuses;
    }

    return payload;
  });
}

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

export function Orders() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedProductId = searchParams.get("productId") || "";
  const selectedVariantId = searchParams.get("variantId") || "";
  const { preset, customRange } = useMemo(
    () => parseReportDateSearchParams(searchParams, { defaultPreset: "All" }),
    [searchParams],
  );
  const [payments, setPayments] = useState<Payment[]>([]);
  const [catalogProducts, setCatalogProducts] = useState<CatalogProduct[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [uniqueCustomers, setUniqueCustomers] = useState(0);
  const [orderPendingDelete, setOrderPendingDelete] = useState<Payment | null>(
    null,
  );
  const [deletingOrderId, setDeletingOrderId] = useState<string | null>(null);
  const [savingAttendanceKey, setSavingAttendanceKey] = useState<string | null>(null);
  const [emailSearch, setEmailSearch] = useState("");
  const debouncedEmailSearch = useDebouncedValue(emailSearch, 300);
  const hasLoadedOnce = useRef(false);

  const productById = useMemo(() => new Map(catalogProducts.map((p) => [p._id, p])), [catalogProducts]);
  const selectedProduct = selectedProductId ? productById.get(selectedProductId) : undefined;
  const selectedProductVariants = selectedProduct?.variants || [];
  const isTicketProductFilter = selectedProduct?.type === ProductTypeEnum.TICKET;
  const [tempDateRange, setTempDateRange] = useState<DayPickerRange | undefined>(undefined);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const { setBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();

  const appendOrdersListFilters = (params: URLSearchParams) => {
    const from = customRange?.from;
    const to = customRange?.to;
    if (from && to) {
      params.append("start", from.toISOString().split("T")[0]!);
      params.append("end", to.toISOString().split("T")[0]!);
    } else {
      params.append("dateRange", preset);
    }
    if (selectedProductId) {
      params.append("productId", selectedProductId);
      if (selectedVariantId) {
        params.append("variantId", selectedVariantId);
      }
    }
    if (debouncedEmailSearch.trim()) {
      params.append("email", debouncedEmailSearch.trim());
    }
  };

  useEffect(() => {
    async function fetchData() {
      setIsLoading(true);
      try {
        const params = new URLSearchParams();
        appendOrdersListFilters(params);
        params.append("page", currentPage.toString());
        params.append("limit", pageSize.toString());
        const url = `/api/payments?${params.toString()}`;
        const response = await fetch(url);

        if (response.ok) {
          const data = await response.json();
          setPayments((data.payments || []) as Payment[]);
          if (data.pagination) {
            setTotalCount(data.pagination.totalCount || 0);
            setTotalPages(data.pagination.totalPages || 0);
          }
          if (data.summary) {
            setTotalRevenue(data.summary.totalRevenue || 0);
            setUniqueCustomers(data.summary.uniqueCustomers || 0);
          }
        } else {
          console.error("Failed to fetch payments");
        }

        setBreadcrumbs([
          { label: "Sales", path: "/products" },
          { label: "Orders", path: "/sales/orders" },
        ]);
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setIsLoading(false);
        hasLoadedOnce.current = true;
      }
    }

    fetchData();

    return () => {
      clearBreadcrumbs();
    };
  }, [preset, customRange, selectedProductId, selectedVariantId, debouncedEmailSearch, currentPage, pageSize]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch("/api/products");
        if (!response.ok || cancelled) return;
        const data = await response.json();
        setCatalogProducts((data.products || []) as CatalogProduct[]);
      } catch (error) {
        console.error("Failed to fetch products:", error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatAmount = (amount: number, currency?: string) => {
    const currencySymbol = currency === "usd" ? "$" : "";
    return `${currencySymbol}${(amount / 100).toFixed(2)}`;
  };

  const getPurchaseDisplayName = ({
    productId: pid,
    variantId: vid,
    description,
  }: PurchasedProductLine) => {
    const desc = description?.trim();
    if (desc) return desc;

    const productId = pid != null ? String(pid) : "";
    if (!productId) {
      return "Unknown product";
    }
    const product = productById.get(productId);
    if (!product) return "Unknown product";
    const variantId = vid != null ? String(vid).trim() : "";
    if (variantId && product.variants?.length) {
      const variant = product.variants.find((v) => String(v._id) === variantId);
      if (variant?.name?.trim()) return variant.name.trim();
    }
    return product.name?.trim() || "Untitled";
  };

  const periods: ReportDatePreset[] = ["1W", "1M", "MTD", "YTD", "All"];

  const getDateRangeForPeriod = (period: ReportDatePreset): { start: Date | null; end: Date | null } => {
    const now = new Date();
    const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);

    switch (period) {
      case "1W": {
        const start = new Date(now);
        start.setDate(start.getDate() - 7);
        start.setHours(0, 0, 0, 0);
        return { start, end };
      }
      case "1M": {
        const start = new Date(now);
        start.setMonth(start.getMonth() - 1);
        start.setHours(0, 0, 0, 0);
        return { start, end };
      }
      case "MTD": {
        const start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
        return { start, end };
      }
      case "YTD": {
        const start = new Date(now.getFullYear(), 0, 1, 0, 0, 0, 0);
        return { start, end };
      }
      case "All":
        return { start: null, end: null };
    }
  };

  const formatPeriodDateRange = (period: ReportDatePreset): string => {
    if (period === "All") return "All";
    const { start, end } = getDateRangeForPeriod(period);
    if (start && end) {
      return `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`;
    }
    return period;
  };

  const handlePeriodClick = (period: ReportDatePreset) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        applyReportDateToSearchParams(next, { preset: period, customRange: undefined });
        return next;
      },
      { replace: true },
    );
    setCurrentPage(1);
  };

  const handleProductFilterChange = (value: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === "all") {
          next.delete("productId");
        } else {
          next.set("productId", value);
        }
        next.delete("variantId");
        return next;
      },
      { replace: true },
    );
    setCurrentPage(1);
  };

  const handleVariantFilterChange = (value: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === "all") {
          next.delete("variantId");
        } else {
          next.set("variantId", value);
        }
        return next;
      },
      { replace: true },
    );
    setCurrentPage(1);
  };

  const handleDateRangeSelect = (range: DayPickerRange | undefined) => {
    setTempDateRange(range);
  };

  const handleDone = () => {
    if (tempDateRange?.from && tempDateRange?.to) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          applyReportDateToSearchParams(next, { preset: "All", customRange: tempDateRange });
          return next;
        },
        { replace: true },
      );
    }
    setCurrentPage(1);
    setCalendarOpen(false);
  };

  const handlePageSizeChange = (value: string) => {
    setPageSize(Number(value));
    setCurrentPage(1);
  };

  const handleCancel = () => {
    setTempDateRange(customRange);
    setCalendarOpen(false);
  };

  const handleOpenChange = (open: boolean) => {
    setCalendarOpen(open);
    if (open) {
      setTempDateRange(customRange);
    }
  };

  const formatDateRange = () => {
    if (customRange?.from && customRange?.to) {
      return `${customRange.from.toLocaleDateString()} - ${customRange.to.toLocaleDateString()}`;
    }
    return "Custom Range";
  };

  const handleConfirmDeleteOrder = async () => {
    const payment = orderPendingDelete;
    if (!payment) return;

    setDeletingOrderId(payment._id);
    try {
      const response = await fetch(`/api/payments/${payment._id}`, {
        method: "DELETE",
      });

      if (response.ok) {
        setPayments((prev) => prev.filter((p) => p._id !== payment._id));
        setOrderPendingDelete(null);
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error("Failed to delete order:", errorData.error);
        alert(errorData.error || "Failed to delete order");
      }
    } catch (error) {
      console.error("Failed to delete order:", error);
      alert("An error occurred while deleting the order");
    } finally {
      setDeletingOrderId(null);
    }
  };

  const handleAttendanceChange = async ({
    payment,
    lineIndex,
    ticketIndex,
    status,
  }: {
    payment: Payment;
    lineIndex: number;
    ticketIndex: number;
    status: TicketAttendanceStatus;
  }) => {
    const savingKey = `${payment._id}-${lineIndex}-${ticketIndex}`;
    setSavingAttendanceKey(savingKey);

    const purchasedProducts = buildPurchasedProductsPayload({ payment, productById });
    const line = purchasedProducts[lineIndex];
    if (!line) {
      setSavingAttendanceKey(null);
      return;
    }

    const statuses = normalizeAttendanceStatuses({
      statuses: line.attendanceStatuses,
      quantity: line.quantity,
    });
    statuses[ticketIndex] = status;
    purchasedProducts[lineIndex] = { ...line, attendanceStatuses: statuses };

    try {
      const response = await fetch(`/api/payments/${payment._id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ purchasedProducts }),
      });

      if (response.ok) {
        const data = await response.json();
        setPayments((prev) =>
          prev.map((p) =>
            p._id === payment._id
              ? { ...p, purchasedProducts: data.payment?.purchasedProducts ?? p.purchasedProducts }
              : p,
          ),
        );
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(typeof errorData.error === "string" ? errorData.error : "Failed to update attendance");
      }
    } catch (error) {
      console.error("Failed to update attendance:", error);
      alert("An error occurred while updating attendance");
    } finally {
      setSavingAttendanceKey(null);
    }
  };

  const handleExportCsv = async () => {
    if (totalCount === 0) return;
    try {
      const params = new URLSearchParams();
      appendOrdersListFilters(params);
      params.append("page", "1");
      params.append("limit", totalCount.toString());
      const response = await fetch(`/api/payments?${params.toString()}`);
      if (!response.ok) {
        console.error("Failed to fetch payments for export");
        return;
      }
      const data = await response.json();
      const allPayments = (data.payments || []) as Payment[];
      if (allPayments.length === 0) return;
      const csv = paymentsDocumentsToCsv(allPayments as unknown as Record<string, unknown>[]);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `orders-export-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to export orders:", error);
    }
  };

  if (isLoading && !hasLoadedOnce.current) {
    return (
      <div className="flex justify-center items-center min-h-[40vh]">
        <Loader2 className="animate-spin h-12 w-12 text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="relative mb-4 max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          type="search"
          placeholder="Search by email"
          value={emailSearch}
          onChange={(e) => {
            setEmailSearch(e.target.value);
            setCurrentPage(1);
          }}
          className="pl-9"
        />
      </div>
      <div className="flex justify-between items-start gap-4 mb-4">
        <div className="flex gap-2 flex-wrap items-center flex-1 min-w-0">
        {periods.map((period) => (
          <Button
            key={period}
            variant={preset === period && !customRange ? "default" : "outline"}
            size="sm"
            onClick={() => handlePeriodClick(period)}
          >
            {period}
          </Button>
        ))}
        <Popover open={calendarOpen} onOpenChange={handleOpenChange}>
          <PopoverTrigger asChild>
            <Button
              variant={customRange || (preset !== "All" && !customRange) ? "default" : "outline"}
              size="sm"
              className="gap-2"
            >
              <CalendarIcon className="h-4 w-4" />
              {customRange?.from && customRange?.to
                ? formatDateRange()
                : preset !== "All" && !customRange
                ? formatPeriodDateRange(preset)
                : "Custom Range"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="range"
              selected={tempDateRange}
              onSelect={handleDateRangeSelect}
              numberOfMonths={1}
              className="rounded-md border-0"
            />
            <div className="flex justify-end gap-2 p-3 border-t">
              <Button variant="outline" size="sm" onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={handleDone}
                disabled={!tempDateRange?.from || !tempDateRange?.to}
              >
                Done
              </Button>
            </div>
          </PopoverContent>
        </Popover>
        <Select
          value={selectedProductId || "all"}
          onValueChange={handleProductFilterChange}
        >
          <SelectTrigger className="w-[220px]" size="sm">
            <SelectValue placeholder="All products" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All products</SelectItem>
            <ProductSelectItems
              products={catalogProducts}
              selectedProductId={selectedProductId || undefined}
            />
          </SelectContent>
        </Select>
        {selectedProductId && selectedProductVariants.length > 0 ? (
          <Select
            value={selectedVariantId || "all"}
            onValueChange={handleVariantFilterChange}
          >
            <SelectTrigger className="w-[220px]" size="sm">
              <SelectValue placeholder="All variants" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All variants</SelectItem>
              {selectedProductVariants.map((variant) => (
                <SelectItem key={variant._id} value={variant._id}>
                  {variant.name?.trim() || "Untitled variant"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : null}
        </div>
        <div className="flex gap-2 shrink-0">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void handleExportCsv()}
            disabled={totalCount === 0}
            className="gap-2"
          >
            <Download className="h-4 w-4" />
            Export CSV
          </Button>
          <Button variant="default" size="sm" asChild>
            <Link to="/sales/orders/new">Create</Link>
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold">
                ${(totalRevenue / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
              <p className="text-muted-foreground mt-2">Revenue</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold">
                {totalCount.toLocaleString()}
              </p>
              <p className="text-muted-foreground mt-2">Orders</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold">
                {uniqueCustomers.toLocaleString()}
              </p>
              <p className="text-muted-foreground mt-2">Customers</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Table containerClassName="overflow-visible">
        <TableHeader>
          <TableRow>
            <TableHead className="sticky top-0 z-10 bg-background pointer-events-none">Email</TableHead>
            <TableHead className="sticky top-0 z-10 bg-background pointer-events-none">Purchases</TableHead>
            <TableHead className="sticky top-0 z-10 bg-background pointer-events-none">
              {isTicketProductFilter ? "Attendance" : "Fulfillment"}
            </TableHead>
            <TableHead className="sticky top-0 z-10 bg-background pointer-events-none">Payment</TableHead>
            <TableHead className="sticky top-0 z-10 bg-background pointer-events-none">Amount</TableHead>
            <TableHead className="sticky top-0 z-10 bg-background pointer-events-none hidden lg:table-cell">Date</TableHead>
            <TableHead className="sticky top-0 z-10 w-[52px] bg-background pointer-events-none" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={7} className="h-24 text-center">
                <Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" />
              </TableCell>
            </TableRow>
          ) : payments && payments.length > 0 ? (
            payments.map((payment) => (
              <TableRow key={payment._id}>
                <TableCell>
                  {payment.customerEmail ? (
                    payment.lead ? (
                      <Link
                        to={`/contacts/${String(payment.lead)}/edit`}
                        className="text-primary underline underline-offset-4"
                      >
                        {payment.customerEmail}
                      </Link>
                    ) : (
                      payment.customerEmail
                    )
                  ) : (
                    "-"
                  )}
                </TableCell>
                <TableCell className="max-w-[280px] text-sm whitespace-normal">
                  {!payment.purchasedProducts?.length ? (
                    "-"
                  ) : (
                    <div className="flex flex-col gap-0.5">
                      {payment.purchasedProducts.map((line, idx) => {
                        const productId = line.productId != null ? String(line.productId) : "";
                        const qty =
                          line.quantity != null && line.quantity > 1
                            ? ` ×${line.quantity}`
                            : "";
                        const name = getPurchaseDisplayName(line);
                        return (
                          <div key={`${payment._id}-${productId || "x"}-${idx}`}>
                            {productId ? (
                              <span className="inline-flex flex-wrap items-baseline gap-x-1">
                                <Link
                                  to={`/products/${productId}`}
                                  className="text-primary underline underline-offset-4"
                                >
                                  {name}
                                </Link>
                                {qty ? (
                                  <span className="text-muted-foreground">{qty}</span>
                                ) : null}
                              </span>
                            ) : (
                              <span>
                                {name}
                                {qty ? (
                                  <span className="text-muted-foreground">{qty}</span>
                                ) : null}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </TableCell>
                <TableCell
                  className="relative z-20 text-sm whitespace-nowrap"
                  onPointerDown={(e) => e.stopPropagation()}
                >
                  {isTicketProductFilter ? (
                    !selectedProductId || !payment.purchasedProducts?.length ? (
                      "-"
                    ) : (
                      (() => {
                        const matchingLines = payment.purchasedProducts
                          .map((line, lineIndex) => ({ line, lineIndex }))
                          .filter(({ line }) =>
                            lineMatchesProductFilter({
                              line,
                              productId: selectedProductId,
                              variantId: selectedVariantId,
                            }),
                          );
                        if (!matchingLines.length) return "-";
                        return (
                          <div className="flex flex-col gap-1.5 min-w-[10rem]">
                            {matchingLines.flatMap(({ line, lineIndex }) => {
                              const quantity = line.quantity ?? 1;
                              const statuses = normalizeAttendanceStatuses({
                                statuses: line.attendanceStatuses,
                                quantity,
                              });
                              return statuses.map((status, ticketIndex) => {
                                const savingKey = `${payment._id}-${lineIndex}-${ticketIndex}`;
                                const isSaving = savingAttendanceKey === savingKey;
                                return (
                                  <Select
                                    key={savingKey}
                                    value={status}
                                    disabled={isSaving}
                                    onValueChange={(value) =>
                                      void handleAttendanceChange({
                                        payment,
                                        lineIndex,
                                        ticketIndex,
                                        status: value as TicketAttendanceStatus,
                                      })
                                    }
                                  >
                                    <SelectTrigger
                                      size="sm"
                                      className="h-8 w-full"
                                      aria-label={
                                        status === "not_checked_in"
                                          ? "Set attendance status"
                                          : ticketAttendanceLabels[status]
                                      }
                                    >
                                      {status !== "not_checked_in" &&
                                        ticketAttendanceLabels[status]}
                                    </SelectTrigger>
                                    <SelectContent position="popper">
                                      {ticketAttendanceSelectOptions.map((opt) => (
                                        <SelectItem key={opt.value} value={opt.value}>
                                          {opt.label}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                );
                              });
                            })}
                          </div>
                        );
                      })()
                    )
                  ) : (
                    formatPaymentFulfillmentStatus(payment.fulfillmentStatus)
                  )}
                </TableCell>
                <TableCell className="text-sm whitespace-nowrap">
                  {formatPaymentStatus(payment.status)}
                </TableCell>
                <TableCell>
                  {formatAmount(payment.amount, payment.currency)}
                </TableCell>
                <TableCell className="hidden lg:table-cell">{formatDate(payment.createdAt)}</TableCell>
                <TableCell className="p-2" onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        aria-label="Order actions"
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem asChild>
                        <Link to={`/sales/orders/${payment._id}`}>
                          <Pencil className="h-4 w-4" />
                          Edit
                        </Link>
                      </DropdownMenuItem>
{payment.paymentIntentId ? (
                      <DropdownMenuItem asChild>
                        <a
                          href={`https://dashboard.stripe.com/payments/${payment.paymentIntentId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <ExternalLink className="h-4 w-4" />
                          Stripe
                        </a>
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem disabled>
                        <ExternalLink className="h-4 w-4" />
                        Stripe
                      </DropdownMenuItem>
                    )}
                    {payment.status !== "complete" ? (
                      <DropdownMenuItem asChild>
                        <Link to={`/sales/orders/${payment._id}/collect`}>
                          <CreditCard className="h-4 w-4" />
                          Collect Payment
                        </Link>
                      </DropdownMenuItem>
                    ) : null}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        variant="destructive"
                        onClick={() => setOrderPendingDelete(payment)}
                      >
                        <Trash2 className="h-4 w-4" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={7} className="h-24 text-center">
                No orders found.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {(totalPages > 1 || totalCount > 0) && (
        <div className="flex items-center justify-between mt-4">
          <div className="flex items-center gap-2">
            <Select value={pageSize.toString()} onValueChange={handlePageSizeChange}>
              <SelectTrigger size="sm" className="w-[80px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-sm text-muted-foreground">
              {totalCount} orders
            </span>
          </div>
          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1 || isLoading}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="text-sm">
                {currentPage} of {totalPages}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages || isLoading}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      )}

      <Dialog
        open={orderPendingDelete !== null}
        onOpenChange={(open) => {
          if (!open) setOrderPendingDelete(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete this order?</DialogTitle>
            <DialogDescription>
              {orderPendingDelete?.customerEmail
                ? `This will permanently remove the order for ${orderPendingDelete.customerEmail}. This cannot be undone.`
                : "This will permanently remove this order. This cannot be undone."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOrderPendingDelete(null)}
              disabled={deletingOrderId !== null}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => void handleConfirmDeleteOrder()}
              disabled={deletingOrderId !== null}
            >
              {orderPendingDelete && deletingOrderId === orderPendingDelete._id
                ? "Deleting..."
                : "Delete order"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
